/** @jsx jsx */
import * as React from 'react';
import Downshift, { DownshiftProps } from 'downshift';
import { PromiseFn } from 'react-async';
import cx from 'classnames';
import Async from 'react-async';
import { jsx, css } from '@emotion/core';
import Caret from '@clearkit/icons/glyphs/Caret';
import Checkmark from '@clearkit/icons/glyphs/Checkmark';
import Highlighter from 'react-highlight-words';
import { FixedSizeList as List } from 'react-window';

import { defaultItemRenderer, defaultEmptyState } from '../../common/utils';
import { usePopover } from '../../hooks';
import { CKSelectListItem } from '../CKSelectListItem';
import {
  ItemBase,
  CKPickerVariant,
  CommonSelectProps,
} from '../../common/types';

interface CKAsyncPickerProps<T extends ItemBase>
  extends DownshiftProps<T>,
    CommonSelectProps<T> {
  loadItems: PromiseFn<T>;
  isSmall?: boolean;
  variant?: CKPickerVariant;
  isDisabled?: boolean;
  itemRenderer?: (item: T) => string;
  initialState?: () => React.ReactNode;
  loadingState?: () => React.ReactNode;
  emptyState?: (inputValue: string) => React.ReactNode;
  maxHeight?: number;
}

function defaultInitialState() {
  return <div className="p-2 text-gray">Enter search query to get started</div>;
}

function defaultLoadingState() {
  return <div className="p-2 text-gray">Loading...</div>;
}

export const CKAsyncPicker = <T extends ItemBase>(
  props: CKAsyncPickerProps<T>
): React.ReactElement<CKAsyncPickerProps<T>> => {
  const {
    loadItems,
    popperOffset,
    popperModifiers,
    popperPlacement,
    placeholder,
    leftIcon,
    isDisabled,
    popperZIndex,
    itemRenderer = defaultItemRenderer,
    itemMetaRenderer = null,
    isItemDisabled = () => false,
    emptyState = defaultEmptyState,
    initialState = defaultInitialState,
    loadingState = defaultLoadingState,
    variant,
    isSmall = false,
    maxHeight = 150,
    ...rest
  } = props;

  const popperReference = React.useRef<HTMLDivElement>(null);
  const popperPopover = React.useRef<HTMLDivElement>(null);

  usePopover({
    popperReference,
    popperPopover,
    popperOffset,
    popperPlacement,
    popperModifiers,
  });

  const listRef = React.useRef<List>();

  return (
    <Downshift
      {...rest}
      onStateChange={changes => {
        if (changes.hasOwnProperty('highlightedIndex') && listRef.current) {
          listRef.current.scrollToItem(changes.highlightedIndex || 0);
        }
      }}
    >
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        getToggleButtonProps,
        isOpen,
        inputValue,
        highlightedIndex,
        selectedItem,
        getRootProps,
        openMenu,
        setItemCount,
      }) => {
        const outerClassName = cx('ck-picker', {
          'ck-picker--open': isOpen,
          'ck-picker--error': variant === 'error',
          'ck-picker--success': variant === 'success',
          'ck-picker--disabled': isDisabled,
          'ck-picker--small': isSmall,
        });

        return (
          <div className={outerClassName} ref={popperReference}>
            <div
              className="ck-picker-toggle ck-picker-toggle--typeable"
              {...getRootProps(
                //@ts-ignore
                {},
                { suppressRefError: true }
              )}
            >
              <label className="sr-only" {...getLabelProps()}>
                {placeholder}
              </label>
              {leftIcon && (
                <span className="ck-picker-icon ck-picker-icon--left">
                  {leftIcon}
                </span>
              )}
              <input
                className="ck-picker-value"
                disabled={isDisabled}
                css={css`
                  width: 100%;
                `}
                {...getInputProps({
                  placeholder,
                  onFocus: () => {
                    if (!isOpen) {
                      openMenu();
                    }
                  },
                })}
              />
              <button
                disabled={isDisabled}
                css={css`
                  display: flex;
                `}
                {...getToggleButtonProps({ type: 'button' })}
                aria-label="toggle menu"
              >
                <span className="ck-picker-icon ck-picker-icon--right">
                  <Caret />
                </span>
              </button>
            </div>
            <div
              css={css`
                visibility: ${isOpen ? 'visible' : 'hidden'};
                pointer-events: ${isOpen ? 'auto' : 'none'};
                z-index: ${popperZIndex};
              `}
              className="ck-popover ck-popover--full-width"
              {...getMenuProps({ ref: popperPopover })}
            >
              <ul
                className="ck-popover-options"
                css={css`
                  outline: 0;
                  padding-top: 0.25rem;
                  padding-bottom: 0.25rem;
                `}
              >
                {!inputValue && isOpen && initialState()}
                {inputValue && isOpen && (
                  <Async
                    onResolve={data => {
                      // @ts-ignore
                      setItemCount(data.length);
                    }}
                    promiseFn={loadItems}
                    watch={inputValue}
                    query={inputValue}
                    // @ts-ignore
                    initialValue={
                      selectedItem && inputValue === selectedItem?.label
                        ? [selectedItem]
                        : undefined
                    }
                  >
                    <Async.Initial>Nothing to show</Async.Initial>
                    <Async.Loading>{loadingState()}</Async.Loading>
                    <Async.Fulfilled<T[]>>
                      {items => (
                        <React.Fragment>
                          {items.length === 0 && emptyState(inputValue)}
                          {items.length > 0 && (
                            <List
                              // @ts-ignore
                              ref={listRef}
                              height={Math.min(
                                items.length * 30 + 4,
                                maxHeight
                              )}
                              itemCount={items.length}
                              itemSize={30}
                              width="100%"
                            >
                              {({ index, style }) => {
                                const item = items[index];
                                return (
                                  <CKSelectListItem
                                    key={`${item}${index}`}
                                    isHighlighted={highlightedIndex === index}
                                    isSelected={
                                      item.value === selectedItem?.value
                                    }
                                    isDisabled={isItemDisabled(item)}
                                    {...getItemProps({
                                      item,
                                      index,
                                      disabled: isItemDisabled(item),
                                      style,
                                    })}
                                    accessoryRenderer={
                                      selectedItem?.value === item.value && (
                                        <Checkmark />
                                      )
                                    }
                                    itemMetaRenderer={
                                      itemMetaRenderer && itemMetaRenderer(item)
                                    }
                                  >
                                    <Highlighter
                                      autoEscape
                                      searchWords={[inputValue || '']}
                                      textToHighlight={itemRenderer(item)}
                                      highlightClassName="ck-highlight"
                                    ></Highlighter>
                                  </CKSelectListItem>
                                );
                              }}
                            </List>
                          )}
                        </React.Fragment>
                      )}
                    </Async.Fulfilled>
                  </Async>
                )}
              </ul>
            </div>
          </div>
        );
      }}
    </Downshift>
  );
};
